<script module lang="ts">
  import type { Snippet } from 'svelte';

  type PopoverProps = {
    class?: string;
    children: Snippet<[{ value: boolean }]> | string;
    floater?: Snippet<[{ value: boolean }]> | string;
    placement?: Placement;
    trigger?: 'hover' | 'focus' | 'always' | 'hidden';
    arrowClass?: string;
  };

  type Arrow = {
    x?: number;
    y?: number;
    centerOffset: number;
  };
</script>

<script lang="ts">
  import {
    computePosition,
    autoUpdate,
    flip,
    offset,
    shift,
    arrow,
    type Placement,
  } from '@floating-ui/dom';
  import clsx from 'clsx';
  import { rune } from '@athena-lib/container-context.svelte';
  const {
    class: className,
    children,
    floater,
    placement = 'top',
    trigger = 'hover',
    arrowClass = 'bg-st-black',
  }: PopoverProps = $props();

  const toolTipOpen = rune(false);

  let targetElement = $state<HTMLElement | undefined>(undefined);
  let floatingElement = $state<HTMLElement | undefined>(undefined);
  let arrowElement = $state<HTMLElement | undefined>(undefined);

  function update() {
    if (!targetElement || !floatingElement) {
      return;
    }
    computePosition(targetElement, floatingElement, {
      placement,
      middleware: [flip(), shift(), offset(8), arrowElement && arrow({ element: arrowElement })],
    }).then(({ x, y, placement, middlewareData }) => {
      if (!floatingElement) {
        return;
      }
      Object.assign(floatingElement.style, {
        left: `${x}px`,
        top: `${y}px`,
      });

      const { x: arrowX, y: arrowY } = middlewareData.arrow as Arrow;

      const staticSide: string | undefined = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right',
      }[placement.split('-')[0] as string];

      if (arrowElement) {
        Object.assign(arrowElement.style, {
          left: arrowX ? `${arrowX}px` : '',
          top: arrowY ? `${arrowY}px` : '',
          right: '',
          bottom: '',
          [staticSide!]: '-4px',
        });
      }
    });
  }

  $effect(() => {
    if (targetElement && floatingElement) {
      return autoUpdate(targetElement, floatingElement, update);
    }
  });

  $effect(() => {
    if (toolTipOpen.value) {
      update();
    }
  });

  function showTooltip() {
    toolTipOpen.value = true;
  }

  function hideTooltip() {
    toolTipOpen.value = false;
  }

  $effect(() => {
    if (trigger === 'always') {
      toolTipOpen.value = true;
    }
    if (trigger === 'hidden') {
      toolTipOpen.value = false;
    }
    if (trigger === 'hover') {
      const listeners = (
        [
          ['mouseenter', showTooltip],
          ['mouseleave', hideTooltip],
          ['focus', showTooltip],
          ['focusin', showTooltip],
          ['focusout', hideTooltip],
          ['blur', hideTooltip],
        ] as const
      ).map(([name, fn]) => {
        // create an unique reference for the listener
        const listener = () => fn();
        return [name, listener] as const;
      });
      listeners.forEach(([name, listener]) => targetElement?.addEventListener(name, listener));
      return () =>
        listeners.forEach(([name, listener]) => targetElement?.removeEventListener(name, listener));
    }
    if (trigger === 'focus') {
      const eventHandler = (event: { target: EventTarget | null }) => {
        // only lose focus if element is not in `floatingElement`, or `targetElement` (or a child of either)
        const isChildOfTarget = targetElement?.contains(event.target as Node | null);
        const isChildOfFloater = floatingElement?.contains(event.target as Node | null);
        if (isChildOfFloater || isChildOfTarget) {
          toolTipOpen.value = true;
        } else {
          toolTipOpen.value = false;
        }
      };

      document.body.addEventListener('focusin', eventHandler);
      document.body.addEventListener('mousedown', eventHandler);
      return () => {
        document.body.removeEventListener('focusin', eventHandler);
        document.body.removeEventListener('mousedown', eventHandler);
      };
    }
  });
</script>

{#if floater}
  <div
    bind:this={floatingElement}
    class={clsx('absolute left-0 top-0 w-max', toolTipOpen.value ? 'block' : 'hidden')}
  >
    {#if typeof floater === 'string'}
      <div class={clsx('rounded bg-st-black px-2 py-1 text-st-white', className)}>
        {floater}
      </div>
    {:else}
      {@render floater(toolTipOpen)}
    {/if}
    <div bind:this={arrowElement} class={clsx(arrowClass, 'absolute h-2 w-2 rotate-45')}></div>
  </div>
{/if}
<div bind:this={targetElement} class={clsx('w-fit', className)}>
  {#if typeof children === 'string'}
    {children}
  {:else}
    {@render children(toolTipOpen)}
  {/if}
</div>
